import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api';
import { isReservedTag, type GetTagsResponse, type UserTagData } from './types';

interface UseUserTagsResult {
  /** Raw response — includes reserved system tags. Use only when you need to
   * detect reserved-tag state (e.g. QuestionFeedbackActions reading thumbs). */
  raw: GetTagsResponse | undefined;
  /** Tags safe to show in pickers, filter pills, inline chips, etc.
   * Reserved system tags (e.g. __thumbsup, __thumbsdown) are excluded. */
  visibleTags: UserTagData[];
  /** Default tag names for the current user role. */
  defaultTags: string[];
  isLoading: boolean;
}

/**
 * Single source of truth for the `/user/tags` query. All consumers that display
 * tag lists (filter pills, pickers, inline chips, bulk-edit, bookmarks) should
 * use `visibleTags` so reserved system tags never leak into UI.
 *
 * Implementation note: shares the React Query cache key with `useItemTags`,
 * so calling both incurs no extra network traffic.
 */
export function useUserTags(): UseUserTagsResult {
  const api = useApi();
  const { data, isLoading } = useQuery({
    queryKey: ['user-tags'],
    queryFn: () => api.fetchApi<GetTagsResponse>('/user/tags'),
    staleTime: 60_000,
  });

  const visibleTags = useMemo(
    () => (data?.tags ?? []).filter(t => !isReservedTag(t.tag)),
    [data],
  );

  return {
    raw: data,
    visibleTags,
    defaultTags: data?.defaultTags ?? [],
    isLoading,
  };
}
